import { DuoAccount, DuoApplication, UserPortalDuoConfig } from 'client/MFA/duo/DuoTypes';
import Api from 'client/api/v2/Api';

function parseResponse(response = {}, cons = x => x) {
  if (!response.data) throw new Error('No data in response');

  return cons(response.data);
}

class DuoApi extends Api {
  constructor() {
    super();
    this.baseUrl = '/api/v2/duo';
    this.userPortalDuo = '/api/userportal/mfa/duo';
  }

  // Accounts

  createAccount() {
    const constructor = acc => new DuoAccount(acc);

    return this.axios.post(`${this.baseUrl}/accounts`, {})
      .then(res => parseResponse(res, constructor))
      .catch(err => Promise.reject(err));
  }

  getAccounts() {
    return this.axios.get(`${this.baseUrl}/accounts`)
      .then(
        res => parseResponse(
          res,
          accounts => accounts.map(account => new DuoAccount(account)),
        ),
      )
      .catch(() => []);
  }

  // Applications

  createApplication(application, accountId) {
    const constructor = app => new DuoApplication(app);
    const duoApplication = new DuoApplication(application);

    return this.axios.post(`${this.baseUrl}/accounts/${accountId}/applications`, duoApplication)
      .then(res => parseResponse(res, constructor))
      .catch(err => Promise.reject(err));
  }

  deleteApplication(applicationId, accountId) {
    if (!accountId) return Promise.reject(Error('No Account ID to delete application configuration'));
    if (!applicationId) return Promise.reject(Error('No Application ID to delete'));

    const constructor = app => new DuoApplication(app);

    return this.axios.delete(`${this.baseUrl}/accounts/${accountId}/applications/${applicationId}`)
      .then(res => parseResponse(res, constructor))
      .catch(() => Promise.reject(Error('Error deleting application')));
  }

  getApplications(accountId) {
    if (!accountId) return Promise.reject(Error('No Account ID to retrieve applications'));

    return this.axios.get(`${this.baseUrl}/accounts/${accountId}/applications`)
      .then(
        res => parseResponse(
          res,
          applications => applications.map(application => new DuoApplication(application)),
        ),
      )
      .catch(() => []);
  }

  saveApplication(duoApplication, accountId) {
    const constructor = app => new DuoApplication(app);
    const applicationId = duoApplication.id;
    const isValidDuoApplication = duoApplication instanceof DuoApplication;
    const application = isValidDuoApplication ? duoApplication : new DuoApplication(duoApplication);

    if (!accountId) return Promise.reject(Error('No Account ID to save application configuration'));
    if (!duoApplication.id) return Promise.reject(Error('No Application ID to modify'));

    return this.axios.put(`${this.baseUrl}/accounts/${accountId}/applications/${applicationId}`, application)
      .then(res => parseResponse(res, constructor))
      .catch(err => Promise.reject(Error(err || 'Error saving application')));
  }

  // User Portal Config

  getUserPortalDuoConfig(applicationId) {
    if (!applicationId) return Promise.reject(Error('No Application ID to retrieve configuration'));

    const constructor = config => new UserPortalDuoConfig({
      ...config,
      id: applicationId,
    });

    return this.axios.get(this.userPortalDuo)
      .then(
        res => parseResponse(res, constructor),
      )
      .catch(() => new UserPortalDuoConfig());
  }

  saveDuoConfig(duoConfig) {
    const isValidDuoConfig = duoConfig instanceof UserPortalDuoConfig;
    const config = isValidDuoConfig ? duoConfig : new UserPortalDuoConfig(duoConfig);

    if (config.enabled && !config.id) return Promise.reject(Error('No application ID for request'));
    if (!config.enabled && config.id) delete config.id;

    return this.axios.put(this.userPortalDuo, config)
      .then(res => parseResponse(res, conf => new UserPortalDuoConfig(conf)))
      .catch(err => Promise.reject(Error(err || 'Could not save Duo Config')));
  }
}

export default new DuoApi();
